"use client";

import { useCallback, useEffect } from "react";
import Script from "next/script";

export type GTMParams = {
  gtmId?: string;
  gtmScriptUrl?: string;
  dataLayerName?: string;
  auth?: string;
  preview?: string;
  nonce?: string;
};

export const GoogleTagManager = (props: GTMParams) => {
  const {
    gtmId = process.env.NEXT_PUBLIC_GTM_ID || "",
    gtmScriptUrl = process.env.NEXT_PUBLIC_GTM_SCRIPT_URL ||
      "https://www.googletagmanager.com/gtm.js",
    dataLayerName = process.env.NEXT_PUBLIC_GTM_DATA_LAYER_NAME || "dataLayer",
    auth,
    preview,
    nonce
  } = props;

  const gtmLayer = dataLayerName !== "dataLayer" ? `&l=${dataLayerName}` : "";
  const gtmAuth = auth ? `&gtm_auth=${auth}` : "";
  const gtmPreview = preview ? `&gtm_preview=${preview}&gtm_cookies_win=x` : "";

  useEffect(() => {
    // https://w3c.github.io/user-timing/#dfn-mark_feature_usage
    performance.mark("mark_feature_usage", {
      detail: {
        feature: "analytics-gtm"
      }
    });
  }, []);

  return gtmId ? (
    <>
      {process.env.NEXT_PUBLIC_COOKIE_TRACE === "1" && (
        <Script id="_gtm_cookie_tracing">
          {`document.__defineSetter__("cookie", (cookie) => {
          console.log("Setting cookie", cookie);
          const setter = Object.getOwnPropertyDescriptor(Document.prototype, "cookie").set;
          setter.call(document, cookie);
        })`}
        </Script>
      )}

      <Script id="_gtm_init">
        {`window["${dataLayerName}"] = window["${dataLayerName}"] || [];`}
        {`function gtag() { window["${dataLayerName}"]?.push(arguments); }`}
        {`gtag("consent", "default", {
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
          analytics_storage: "denied",
          wait_for_update: 500
        });`}
      </Script>

      <Script
        id="_gtm"
        data-ntpc="GTM"
        src={`${gtmScriptUrl}?id=${gtmId}${gtmLayer}${gtmAuth}${gtmPreview}`}
        nonce={nonce}
      />

      <Script id="_gtm_start">
        {`gtag("js", new Date());`}
        {`gtag("config", "${gtmId}");`}
        {`gtag("set", "url_passthrough", true);`}
      </Script>
    </>
  ) : null;
};

type EventName = "click" | "submit" | "purchase" | "page_view" | "screen_view";

type GtagFunctionArgs =
  [GtagCommand, EventName | EventParams] | [GtagCommand, string, EventParams];

type GtagCommand = "config" | "set" | "js" | "event" | "consent";

interface EventParams {
  [key: string]: unknown;
}

const dataLayerHolder = globalThis as unknown as {
  gtag?: (...args: GtagFunctionArgs) => void;
};

export const useSendAnalyticsEvent = () => {
  const sendEvent = useCallback((...args: GtagFunctionArgs) => {
    if (!dataLayerHolder.gtag) {
      return;
    }
    dataLayerHolder.gtag(...args);
  }, []);

  return sendEvent;
};
